import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { sendMagicLink } from '@/lib/api/endpoints';
import { toUserMessage } from '@/lib/api/error';
import { useUserAuth } from '@/lib/auth/UserAuthContext';

/**
 * Magic-link request page.
 *
 * Submitting sends a sign-in email; in dev/test the API also returns a
 * `consume_url` and we follow it directly so the local flow has no
 * inbox dependency. In production the response carries no link and we
 * surface a "check your email" confirmation.
 */
export function SignIn(): React.JSX.Element {
  const { status } = useUserAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [sent, setSent] = React.useState<{ devLink: string | null } | null>(null);

  React.useEffect(() => {
    if (status === 'authenticated') {
      navigate('/today', { replace: true });
    }
  }, [status, navigate]);

  const onSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (submitting || email.trim().length === 0) return;
      setSubmitting(true);
      setError(null);
      void (async () => {
        try {
          const resp = await sendMagicLink({ email: email.trim() });
          setSent({ devLink: resp.consume_url });
        } catch (err) {
          setError(toUserMessage(err, 'Could not send sign-in link.'));
        } finally {
          setSubmitting(false);
        }
      })();
    },
    [email, submitting],
  );

  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <Card className="w-full max-w-sm">
        <CardHeader>
          <CardTitle>Sign in</CardTitle>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-3" data-testid="signin-sent">
              <p className="text-body">
                Check your email — we sent a sign-in link to{' '}
                <span className="font-medium">{email.trim()}</span>. The link works once and expires
                in 15 minutes.
              </p>
              {sent.devLink ? (
                <p className="text-caption text-muted-foreground">
                  Dev mode:{' '}
                  <a className="underline" href={sent.devLink} data-testid="signin-dev-link">
                    open the link
                  </a>
                </p>
              ) : null}
              <p className="text-caption text-muted-foreground">
                Lost access to your email?{' '}
                <Link to="/recover" className="underline">
                  Use a recovery code
                </Link>
                .
              </p>
            </div>
          ) : (
            <form className="space-y-4" onSubmit={onSubmit} noValidate>
              <div className="space-y-1">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  disabled={submitting}
                />
              </div>
              {error ? (
                <p role="alert" className="text-caption text-destructive">
                  {error}
                </p>
              ) : null}
              <Button
                type="submit"
                className="w-full"
                disabled={submitting || email.trim().length === 0}
              >
                {submitting ? 'Sending…' : 'Email me a sign-in link'}
              </Button>
              <p className="text-caption text-muted-foreground">
                Already have a recovery code?{' '}
                <Link to="/recover" className="underline">
                  Use it instead
                </Link>
                .
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
